import { useEffect, useRef, useState } from "react";
import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import * as wishlistItemsService from "../services/wishlistItemsService";
import { useRecipientOptions, useAddSurpriseItem } from "../hooks/useClaimedItems";
import ThumbnailPicker from "./ThumbnailPicker";
import { digitsToNumber, digitsToDisplay, handleDigitsChange } from "../utils/priceMask";
import { validateAndResizeImage } from "../utils/thumbnailValidation";

// Shared "Add Surprise Item" entry point — renders its own trigger button plus the
// add-flow dialog. Used from ClaimedItems.js (no default recipient) and NameDetails.js
// (defaults to, but doesn't lock in, the wishlist being viewed). Either way it only ever
// adds to the current user's own Shopping List — see useAddSurpriseItem(userId).
export default function SurpriseItemDialog({ userId, groupId, defaultRecipientNameId = null }) {
  const [open, setOpen] = useState(false);
  const [selectedRecipientKey, setSelectedRecipientKey] = useState("");
  const [itemText, setItemText] = useState("");
  const [url, setUrl] = useState("");
  const [priceDigits, setPriceDigits] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [visibleToGroup, setVisibleToGroup] = useState(true);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  // Only auto-apply defaultRecipientNameId once per dialog-open session, so a later
  // recipients refetch (while the dialog is still open) can't clobber a manual change.
  const appliedDefaultRef = useRef(false);

  const { data: recipients = [], isFetching: recipientsLoading } = useRecipientOptions(groupId, userId, open);
  const addSurpriseMutation = useAddSurpriseItem(userId);

  useEffect(() => {
    if (!open || !defaultRecipientNameId || appliedDefaultRef.current) return;
    const match = recipients.find((r) => r.nameId === defaultRecipientNameId);
    if (match) {
      setSelectedRecipientKey(match.key);
      appliedDefaultRef.current = true;
    }
  }, [open, defaultRecipientNameId, recipients]);

  const openDialog = () => {
    setItemText("");
    setUrl("");
    setPriceDigits("");
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setPhotoError("");
    setSelectedRecipientKey("");
    setVisibleToGroup(true);
    setFormError("");
    appliedDefaultRef.current = false;
    setOpen(true);
  };

  const handleFileSelected = async (file) => {
    const { blob, error } = await validateAndResizeImage(file, 'SurpriseItemDialog');
    if (!blob) {
      setPhotoError(error);
      return;
    }
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailFile(blob);
    setThumbnailPreview(URL.createObjectURL(blob));
    setPhotoError("");
  };

  const clearThumbnail = () => {
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setPhotoError("");
  };

  const submitSurpriseItem = async () => {
    const trimmedText = itemText.trim();
    if (!trimmedText || !selectedRecipientKey) {
      setFormError("Please enter an item name and choose a recipient.");
      return;
    }

    const recipient = recipients.find((r) => r.key === selectedRecipientKey);
    setSaving(true);
    setFormError("");

    const price = digitsToNumber(priceDigits);

    let thumbnailUrl = null;
    if (thumbnailFile) {
      try {
        thumbnailUrl = await wishlistItemsService.uploadThumbnail(thumbnailFile, "surprise", userId);
      } catch {
        setPhotoError("Photo upload failed. Try again, or remove the photo to add the item without one.");
        setSaving(false);
        return;
      }
    }

    try {
      await addSurpriseMutation.mutateAsync({
        itemText: trimmedText,
        url: url.trim() || null,
        price,
        userId,
        thumbnailUrl,
        recipientLabel: recipient.label,
        recipientNameId: recipient.nameId,
        visibleToGroup,
      });
      setOpen(false);
    } catch {
      // Error already logged in claimedItemsService; clean up the orphaned upload, if any.
      if (thumbnailUrl) await wishlistItemsService.deleteThumbnail(thumbnailUrl);
      setFormError("Something went wrong adding this item. Please try again.");
    }
    setSaving(false);
  };

  return (
    <>
      <Tooltip title={groupId ? "" : "Join a group to add a surprise item"}>
        <span>
          <Button
            variant="contained"
            onClick={openDialog}
            disabled={!groupId}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Add Surprise Item
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Surprise Item</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Use this only to log a surprise gift you're buying for someone in your group — something that
            isn't on their wishlist. It goes straight into your Claimed Items. Use the checkbox below to
            choose whether it also shows up read-only on their own Wishlist page to the rest of the group —
            either way, it never appears on the list of the person it's for.
          </Typography>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <ThumbnailPicker
                preview={thumbnailPreview}
                onFileSelected={handleFileSelected}
                onClear={clearThumbnail}
                error={photoError}
              />
              <Stack spacing={2} sx={{ flex: 1 }}>
                <TextField
                  label="Item"
                  value={itemText}
                  onChange={(e) => setItemText(e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Optional URL"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  fullWidth
                />
              </Stack>
            </Stack>
            <TextField
              label="Price"
              value={digitsToDisplay(priceDigits)}
              onChange={handleDigitsChange(setPriceDigits)}
              inputMode="numeric"
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <FormControl fullWidth required>
              <InputLabel>Who is this for?</InputLabel>
              <Select
                label="Who is this for?"
                value={selectedRecipientKey}
                onChange={(e) => setSelectedRecipientKey(e.target.value)}
              >
                {recipientsLoading ? (
                  <MenuItem value="" disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : recipients.length === 0 ? (
                  <MenuItem value="" disabled>
                    No group members or child lists found
                  </MenuItem>
                ) : (
                  recipients.map((r) => (
                    <MenuItem key={r.key} value={r.key}>
                      {r.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleToGroup}
                  onChange={(e) => setVisibleToGroup(e.target.checked)}
                />
              }
              label="Show this item on their Wishlist page to the rest of the group"
            />
            {formError && (
              <Typography variant="caption" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={submitSurpriseItem}>
            {saving ? "Adding..." : "Add Surprise Item"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
